import {
    MAX_FILE_SIZE_GB,
    MAX_TOTAL_SIZE_GB,
    MAX_SINGLE_FILE_SIZE_GB,
    MAX_FILE_COUNT
} from './uploadConfig';

/**
 * Upload limit type identifiers used in validateUploadLimits results.
 */
export const UPLOAD_LIMIT_TYPES = {
    FILE_COUNT: 'fileCount',
    TOTAL_SIZE: 'totalSize',
    SINGLE_FILE_SIZE: 'singleFileSize'
};

/**
 * Upload limit definitions — drives validateUploadLimits messaging.
 * Each entry exposes the type key, the configured limit, and a message
 * builder. Builders receive a context object with the values needed to
 * format the message (e.g. totalFiles, totalSize, formatSize, fileName).
 */
export const UPLOAD_LIMITS = {
    [UPLOAD_LIMIT_TYPES.FILE_COUNT]: {
        type: UPLOAD_LIMIT_TYPES.FILE_COUNT,
        limit: MAX_FILE_COUNT,
        buildMessage: ({ totalFiles }) =>
            `Total file count (${totalFiles.toLocaleString()}) exceeds limit of ${MAX_FILE_COUNT.toLocaleString()} files`
    },
    [UPLOAD_LIMIT_TYPES.TOTAL_SIZE]: {
        type: UPLOAD_LIMIT_TYPES.TOTAL_SIZE,
        limit: MAX_TOTAL_SIZE_GB,
        buildMessage: ({ formattedSize }) =>
            `Total dataset size (${formattedSize}) exceeds limit of ${MAX_TOTAL_SIZE_GB} GB`
    },
    [UPLOAD_LIMIT_TYPES.SINGLE_FILE_SIZE]: {
        type: UPLOAD_LIMIT_TYPES.SINGLE_FILE_SIZE,
        limit: MAX_SINGLE_FILE_SIZE_GB,
        buildMessage: ({ fileName, formattedSize }) =>
            `File "${fileName}" (${formattedSize}) exceeds limit of ${MAX_SINGLE_FILE_SIZE_GB} GB per file`
    }
};

/**
 * System files to ignore (macOS, Windows, Linux metadata files).
 * These are silently skipped during upload validation.
 */
export const SYSTEM_FILES = new Set([
    '.DS_Store',                 // macOS folder metadata
    'Thumbs.db',                 // Windows thumbnail cache
    'desktop.ini',               // Windows folder settings
    '.localized',                // macOS localization
    '.Spotlight-V100',           // macOS Spotlight index
    '.Trashes',                  // macOS trash folder
    '.fseventsd',                // macOS filesystem events
    '.TemporaryItems',           // macOS temporary items
    '.VolumeIcon.icns',          // macOS volume icons
    '.apdisk',                   // macOS AFP disk
    '$RECYCLE.BIN',              // Windows recycle bin
    'System Volume Information', // Windows system volume info
    '.directory',                // KDE directory settings
    '._.DS_Store'                // macOS resource fork for .DS_Store
]);

/**
 * Skip category definitions — drives all buildSkipMessage formatting.
 * Each entry maps a skippedFiles key to its message templates.
 *
 * Templates receive (count, fileList) where fileList is already a
 * truncated/joined string, keeping this module free of util deps.
 */
export const SKIP_CATEGORIES = [
    {
        key: 'large',
        countLabel: (n) => `${n} exceeding ${MAX_FILE_SIZE_GB}GB`,
        singleMsg: (count, fileList) => `Skipped ${count} file(s) exceeding ${MAX_FILE_SIZE_GB}GB: ${fileList}`,
        multiLine: (count, fileList) => `- ${count} file(s) exceeding ${MAX_FILE_SIZE_GB}GB: ${fileList}`
    },
    {
        key: 'zeroSize',
        countLabel: (n) => `${n} zero-size`,
        singleMsg: (count, fileList) => `Skipped ${count} zero-size file(s): ${fileList}`,
        multiLine: (count, fileList) => `- ${count} zero-size file(s): ${fileList}`
    },
    {
        key: 'invalidExtension',
        countLabel: (n) => `${n} invalid extension`,
        singleMsg: (count, fileList) => `Skipped ${count} file(s) with invalid extension: ${fileList}`,
        multiLine: (count, fileList) => `- ${count} file(s) with invalid extension: ${fileList}`
    },
    {
        key: 'undetectedType',
        countLabel: (n) => `${n} undetected file type`,
        singleMsg: (count, fileList) => `Skipped ${count} file(s) with undetected file type: ${fileList}`,
        multiLine: (count, fileList) => `- ${count} file(s) with undetected file type: ${fileList}`
    }
];

// Helper to get extensions from Redux — NO fallback
const getSupportedExtensions = () => {
    try {
        const store = require('../redux/store').default;
        const state = store.getState();
        const { initialized, extensions } = state.fileExtensions;
        
        if (initialized && extensions.length > 0) {
            return new Set(extensions);
        }
    } catch {
        // Redux not available
    }
    
    // Return empty set — upload button should be disabled if API failed
    return new Set();
};

/**
 * Check if a file extension is allowed
 * Reads from Redux (API) — no fallback
 */
export const isExtensionAllowed = (fileName) => {
    const allowedExtensions = getSupportedExtensions();
    
    // If no extensions loaded, reject all files
    if (allowedExtensions.size === 0) {
        return false;
    }
    
    const lastDotIndex = fileName.lastIndexOf('.');
    
    if (lastDotIndex === -1 || lastDotIndex === 0) {
        return allowedExtensions.has("");
    }
    
    const extension = fileName.substring(lastDotIndex + 1).toLowerCase();
    return allowedExtensions.has(extension);
};

/**
 * Get the extension from a filename
 */
export const getFileExtension = (fileName) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    
    if (lastDotIndex === -1 || lastDotIndex === 0) {
        return "";
    }
    
    return fileName.substring(lastDotIndex + 1).toLowerCase();
};